import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Stack,
} from '@mui/material'
import { Theme } from '@rjsf/mui'
import { withTheme } from '@rjsf/core'
import { FormTemplate, FormFieldType, FormField } from '@/core/models/form.model'
import FormSubmissionDialog from './FormSubmissionDialog'
import TextInput from '@/components/ui/inputs/TextInput'
import SelectInput from '@/components/ui/inputs/SelectInput'
import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'
import MultipleCheckboxInput from '@/components/ui/inputs/MultipleCheckboxInput'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import DateTimePickerInput from '@/components/ui/inputs/DateTimePickerInput'
import dayjs from 'dayjs'

const ThemedForm = withTheme(Theme)

interface FormSubmissionProps {
    template: FormTemplate
    formData: Record<string, any>
    autoSaveStatus: 'saving' | 'saved' | 'error'
    validationErrors: Record<string, string>
    isConfirmDialogOpen: boolean
    submissionInProgress: boolean
    onFormChange: (data: Record<string, any>) => void
    onSubmit: () => void
    onConfirmDialogClose: () => void
    onConfirmSubmit: () => void
}

const FormFieldComponent = React.memo(({
    field,
    value,
    error,
    onChange
}: {
    field: FormField
    value: any
    error?: string
    onChange: (value: any) => void
}) => {
    const { t } = useTranslation()
    switch (field.type) {
        case FormFieldType.Text:
            return (
                <TextInput
                    name={field.id}
                    label={field.label}
                    required={field.required}
                    error={!!error}
                    helperText={error}
                    value={value ?? ''}
                    onChange={e => onChange(e.target.value)}
                />
            )
        case FormFieldType.TextArea:
            return (
                <TextInput
                    name={field.id}
                    label={field.label}
                    required={field.required}
                    error={!!error}
                    helperText={error}
                    value={value ?? ''}
                    onChange={e => onChange(e.target.value)}
                    multiline
                    rows={4}
                />
            )
        case FormFieldType.Multiple:
            return (
                <MultipleCheckboxInput
                    name={field.id}
                    label={field.label}
                    error={!!error}
                    helperText={error}
                    value={Array.isArray(value) ? value : []}
                    onChange={(selectedValues) => onChange(selectedValues)}
                    options={field.options?.map(opt => ({ value: opt, label: opt })) ?? []}
                />
            )
        case FormFieldType.Number:
            return (
                <TextInput
                    name={field.id}
                    label={field.label}
                    required={field.required}
                    error={!!error}
                    helperText={error}
                    value={value ?? ''}
                    onChange={e => onChange(e.target.value)}
                    type="number"
                />
            )
        case FormFieldType.Boolean:
            return (
                <RadioGroupInput
                    name={field.id}
                    label={field.label}
                    error={!!error}
                    helperText={error}
                    value={value ? 'true' : 'false'}
                    onChange={(e) => onChange(e.target.value === 'true')}
                    options={[
                        { label: t('page.forms.fields.no'), value: 'false' },
                        { label: t('page.forms.fields.yes'), value: 'true' }
                    ]}
                    row={true}
                />
            )
        case FormFieldType.Date:
            return (
                <DatePickerInput
                    name={field.id}
                    label={field.label}
                    error={!!error}
                    helperText={error}
                    value={value ? dayjs(value) : null}
                    onChange={(dateValue) => onChange(dateValue?.toDate() || null)}
                />
            )
        case FormFieldType.DateTime:
            return (
                <DateTimePickerInput
                    name={field.id}
                    label={field.label}
                    error={!!error}
                    helperText={error}
                    value={value ? dayjs(value) : null}
                    onChange={(dateValue) => onChange(dateValue?.toDate() || null)}
                />
            )
        default:
            return null
    }
})

FormFieldComponent.displayName = 'FormFieldComponent'

const FormSubmissionComponent: React.FC<FormSubmissionProps> = ({
    template,
    formData,
    autoSaveStatus,
    validationErrors,
    isConfirmDialogOpen,
    submissionInProgress,
    onFormChange,
    onSubmit,
    onConfirmDialogClose,
    onConfirmSubmit,
}) => {
    const { t } = useTranslation()

    const handleFieldChange = useCallback((fieldId: string) => (value: any) => {
        onFormChange({ ...formData, [fieldId]: value })
    }, [formData, onFormChange])

    const validationErrorsList = useMemo(() => {
        return Object.entries(validationErrors).map(([fieldId, error]) => {
            const field = template.fields.find(f => f.id === fieldId)
            return {
                key: fieldId,
                label: field?.label || fieldId,
                error
            }
        })
    }, [validationErrors, template.fields])

    const autoSaveAlert = useMemo(() => {
        switch (autoSaveStatus) {
            case 'saving':
                return (
                    <Alert severity="info" icon={<CircularProgress size={20} />}>
                        {t('page.forms.submission.savingDraft')}
                    </Alert>
                )
            case 'saved':
                return (
                    <Alert severity="success">{t('page.forms.submission.draftSaved')}</Alert>
                )
            case 'error':
                return (
                    <Alert severity="error">{t('page.forms.submission.errorSavingDraft')}</Alert>
                )
            default:
                return null
        }
    }, [autoSaveStatus, t])

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {template.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    {template.description}
                </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
                {autoSaveAlert}
            </Box>

            {validationErrorsList.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Alert severity="error">
                        <Typography variant="subtitle2" gutterBottom>
                            {t('page.forms.submission.fixErrors')}
                        </Typography>
                        <ul>
                            {validationErrorsList.map(({ key, label, error }) => (
                                <li key={key}>{label}: {error}</li>
                            ))}
                        </ul>
                    </Alert>
                </Box>
            )}

            <Stack spacing={3}>
                {template.fields.map(field => (
                    <FormFieldComponent
                        key={field.id}
                        field={field}
                        value={formData[field.id]}
                        error={validationErrors[field.id]}
                        onChange={handleFieldChange(field.id)}
                    />
                ))}
            </Stack>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                    data-testid="form-submit"
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                    disabled={submissionInProgress}
                >
                    {submissionInProgress ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            {t('page.forms.submission.submitting')}
                        </>
                    ) : (
                        t('page.forms.submission.submit')
                    )}
                </Button>
            </Box>

            <FormSubmissionDialog
                open={isConfirmDialogOpen}
                onClose={onConfirmDialogClose}
                onConfirm={onConfirmSubmit}
                template={template}
                responses={formData}
                validationErrors={validationErrors}
            />
        </Paper>
    )
}

export default React.memo(FormSubmissionComponent) 